// janus-engine/statuses/class-aura.ts — CLASS AURA STANCE, TS twin of
// includes/functions/janus/statuses/class_aura_stance.php (2026-09-24).
//
// A class aura is an always-on follow aura the creature CHOOSES: at most ONE class-aura anchor per
// creature; activating another replaces it (a reaction in combat, free out of combat); its radius is
// aoe_distance + the carrier's aura_distance_mod integer fold. Plan: plans/class-aura-stance-rework-2026-09-24.md.
//
// Pure functions only. tools/test/janus-class-aura-stance-parity.test.mjs runs one case table through this
// file and the PHP oracle; a rule changed in one lane fails there. The M4c zones / reactions slices of the
// client-truth migration call THESE when the PHP call sites retire.

import { isSelfStatusOrigin } from './status-origin.js';

export type LayerValueReader = (definition: unknown, layer: string) => unknown;

export type ClassAuraStanceAction = 'noop' | 'refuse' | 'replace' | 'install';
export interface ClassAuraStanceDecision {
    action: ClassAuraStanceAction;
    reason: 'already_active' | 'no_reaction' | 'other_active' | 'none_active';
    old_anchor: Record<string, unknown> | null;
}

const AURA_QUARTET = ['aoe_type', 'aoe_distance', 'persistent_aoe', 'aoe_follows_target'] as const;

const scalarOf = (value: unknown): unknown => (Array.isArray(value) ? value[0] : value);
const numberOf = (value: unknown): number => {
    const v = scalarOf(value);
    if (v === null || v === undefined || v === '') return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};
const idOf = (value: unknown): string => {
    const v = scalarOf(value);
    return typeof v === 'string' ? v.trim().toUpperCase() : '';
};

/** ft_item_is_class_aura twin: the aura quartet PLUS class_aura = 1 (the flag sits BESIDE the quartet). */
export function isClassAuraDefinition(definition: unknown, layerValue: LayerValueReader): boolean {
    if (!definition || typeof definition !== 'object') return false;
    for (const layer of AURA_QUARTET) {
        if (layerValue(definition, layer) == null) return false;
    }
    return numberOf(layerValue(definition, 'class_aura')) === 1;
}

/**
 * ft_class_aura_effective_radius twin: authored aoe_distance + the carrier's aura_distance_mod, never below
 * the authored radius. `authoredRadius` is the item_layer_types value (not a computed override).
 */
export function classAuraEffectiveRadius(authoredRadius: unknown, carrierAuraDistanceMod: unknown): number {
    const base = numberOf(authoredRadius);
    const mod = numberOf(carrierAuraDistanceMod);
    return Math.max(base, base + mod);
}

/**
 * ft_class_aura_active_anchor twin over STRUCTURAL status rows (the lossless target list — never the payload
 * view, which filters hostile self-anchors out). A row is the carrier's class-aura anchor when
 * status_aoe_source == 1, caster == target == carrier, and its resolved definition is a class aura.
 */
export function classAuraActiveAnchor(
    carrierId: unknown,
    targetStatusRows: ReadonlyArray<Record<string, unknown>>,
    layerValue: LayerValueReader,
    resolveDefinition?: (originId: string) => unknown,
): Record<string, unknown> | null {
    const carrier = idOf(carrierId);
    if (carrier === '') return null;
    for (const row of targetStatusRows) {
        if (!row || typeof row !== 'object') continue;
        if (numberOf(row.status_deleted) === 1) continue;
        if (numberOf(row.status_aoe_source) !== 1) continue;
        if (idOf(row.status_caster_id) !== carrier || idOf(row.status_target_id) !== carrier) continue;
        let def: unknown = row.status_arr && typeof row.status_arr === 'object' ? row.status_arr : null;
        if (def === null && resolveDefinition) {
            const origin = isSelfStatusOrigin(idOf(row.status_id), idOf(row.status_origin_id)) ? idOf(row.status_item_id)
                : idOf(row.status_origin_id) || idOf(row.status_item_id);
            def = origin ? resolveDefinition(origin) : null;
        }
        if (isClassAuraDefinition(def, layerValue)) return row;
    }
    return null;
}

/** ft_class_aura_stance_decision twin — THE stance rule. */
export function classAuraStanceDecision(
    activeAnchor: Record<string, unknown> | null,
    requestedItemId: unknown,
    inCombat: boolean,
    reaction: number,
): ClassAuraStanceDecision {
    const requested = idOf(requestedItemId);
    const activeId = activeAnchor ? (isSelfStatusOrigin(idOf(activeAnchor.status_id), idOf(activeAnchor.status_origin_id))
        ? idOf(activeAnchor.status_item_id) : idOf(activeAnchor.status_origin_id) || idOf(activeAnchor.status_item_id)) : '';
    if (activeId !== '' && activeId === requested) return { action: 'noop', reason: 'already_active', old_anchor: activeAnchor };
    if (inCombat && reaction <= 0) return { action: 'refuse', reason: 'no_reaction', old_anchor: activeAnchor };
    if (activeId !== '') return { action: 'replace', reason: 'other_active', old_anchor: activeAnchor };
    return { action: 'install', reason: 'none_active', old_anchor: null };
}
